import {
  ApiError,
  AuthenticationRequiredError,
  authenticationScreen,
  type Account,
  type Organization,
} from "@groundspark/browser-auth";
import { createBrandGraphic } from "@groundspark/brand";
import {
  button,
  buttonIcon,
  dashboardHeader,
  dashboardSelector,
  dashboardTabs,
  element,
  heading,
  table,
} from "@groundspark/browser-ui";
import type { AuthManager } from "./auth";
import { BranchApi } from "./branch-api";
import { channelIcon } from "./channel-icon";
import { CommunicationComposer } from "./communication-composer";
import { CommunicationHistory } from "./communication-history";
import { confirmDeletion } from "./confirm-dialog";
import {
  channels,
  channelLabels,
  type Branch,
  type Channel,
  type Communication,
  type CommunicationDraft,
  type Leaflet,
  type LeafletDocument,
  type Member,
} from "./domain";
import { emailDisplayText } from "./email-content";
import { LeafletAnalytics } from "./leaflet-analytics";
import { type PaperSize, paperSizeLabels } from "./leaflet-pages";
import { leafletSettingsDialog } from "./leaflet-settings-dialog";
import { LeafletWorkspace } from "./leaflet-workspace";
import { memberTable } from "./member-table";
import { newLeafletDialog } from "./new-leaflet-dialog";
import type { BranchStatistics } from "./statistics";
import { statisticsPanel } from "./statistics-panel";

type Tab = "members" | "communications" | "leaflets" | "statistics";
type ListTab = "members" | "communications" | "leaflets";
interface TabLoad {
  request: AbortController | null;
  error: string | null;
}
const loadingMessages: Record<Tab, string> = {
  members: "Loading branch members…",
  communications: "Loading communications…",
  leaflets: "Loading leaflets…",
  statistics: "Loading campaign statistics…",
};
const times = new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short" });
const numbers = new Intl.NumberFormat("en-GB");

export class BranchApplication {
  private readonly api: BranchApi;
  private branches: readonly Branch[] | null = null;
  private organizations: readonly Organization[] | null = null;
  private branchId: string | null = null;
  private members: readonly Member[] | null = null;
  private communications: readonly Communication[] | null = null;
  private leaflets: readonly Leaflet[] | null = null;
  private statistics: BranchStatistics | null = null;
  private readonly tabLoads: Record<Tab, TabLoad> = {
    members: { request: null, error: null },
    communications: { request: null, error: null },
    leaflets: { request: null, error: null },
    statistics: { request: null, error: null },
  };
  private readonly cursors: Record<ListTab, string | null> = {
    members: null,
    communications: null,
    leaflets: null,
  };
  private activeTab: Tab = "members";
  private busy = false;
  private error: string | null = null;
  private request: AbortController | null = null;
  private composer: CommunicationComposer | null = null;
  private leafletWorkspace: LeafletWorkspace | null = null;
  private leafletAnalytics: LeafletAnalytics | null = null;
  private history: CommunicationHistory | null = null;
  private leavingWorkspace = false;

  public constructor(
    private readonly root: HTMLElement,
    private readonly authentication: AuthManager,
  ) {
    this.api = new BranchApi(authentication);
  }

  public async start(): Promise<void> {
    this.renderMessage("Verifying your session", "Please wait a moment.");
    try {
      if (await this.authentication.initialize()) await this.reload(false);
      else this.renderSignIn();
    } catch (error) {
      this.renderSignIn(message(error));
    }
  }

  public destroy(): void {
    this.request?.abort();
    this.request = null;
    this.clearData();
    this.root.replaceChildren();
  }

  private clearData(): void {
    this.members = null;
    this.communications = null;
    this.leaflets = null;
    this.statistics = null;
    for (const load of Object.values(this.tabLoads)) {
      load.request?.abort();
      load.request = null;
      load.error = null;
    }
    this.cursors.members = null;
    this.cursors.communications = null;
    this.cursors.leaflets = null;
    this.composer?.destroy();
    this.composer = null;
    this.leafletWorkspace?.destroy();
    this.leafletWorkspace = null;
    this.closeLeafletAnalytics();
    this.history?.destroy();
    this.history = null;
  }

  private beginRequest(): AbortController {
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    this.busy = true;
    this.error = null;
    return request;
  }

  private async reload(refreshAccount = true): Promise<void> {
    if (this.workspace !== null && !(await this.closeWorkspace())) return;
    const request = this.beginRequest();
    this.clearData();
    this.branches = null;
    this.organizations = null;
    this.renderDashboard();
    try {
      if (refreshAccount) await this.authentication.refreshAccount();
      const account = this.authentication.account;
      if (account === null || account.role === "member" || request.signal.aborted) return;
      const [branches, organizations] = await Promise.all([
        this.api.listBranches(request.signal),
        account.role === "superadmin"
          ? this.authentication.organizations()
          : Promise.resolve(null),
      ]);
      if (request.signal.aborted) return;
      this.branches = branches;
      this.organizations = organizations;
      if (!branches.some((branch) => branch.id === this.branchId))
        this.branchId = branches[0]?.id ?? null;
    } catch (error) {
      if (request.signal.aborted) return;
      if (!this.handleFailure(error)) this.error = message(error);
    } finally {
      if (this.request === request) {
        this.busy = false;
        this.renderDashboard();
      }
    }
    if (this.request === request && this.branches !== null) await this.loadTab(this.activeTab);
  }

  private async selectBranch(branchId: string): Promise<void> {
    if (this.busy || !this.branches?.some((branch) => branch.id === branchId)) return;
    if (this.workspace !== null && !(await this.closeWorkspace())) {
      this.renderDashboard();
      return;
    }
    this.request?.abort();
    this.request = null;
    this.error = null;
    this.clearData();
    this.branchId = branchId;
    await this.loadTab(this.activeTab);
  }

  private async selectOrganization(organizationId: string): Promise<void> {
    if (this.busy) return;
    if (this.workspace !== null && !(await this.closeWorkspace())) {
      this.renderDashboard();
      return;
    }
    const request = this.beginRequest();
    this.clearData();
    this.branches = null;
    this.branchId = null;
    this.organizations = null;
    this.renderDashboard();
    try {
      await this.authentication.selectOrganization(organizationId);
      if (!request.signal.aborted) await this.reload(false);
    } catch (error) {
      if (request.signal.aborted) return;
      this.handleFailure(error);
      this.renderSignIn(message(error));
    }
  }

  private async loadTab(tab: Tab, more = false): Promise<void> {
    const load = this.tabLoads[tab];
    if (load.request !== null || this.branchId === null) return;
    const cursor = more && tab !== "statistics" ? this.cursors[tab] : null;
    if (more && cursor === null) return;
    const request = new AbortController();
    load.request = request;
    load.error = null;
    this.renderTabUpdate();
    try {
      if (tab === "statistics") {
        const statistics = await this.api.statistics(this.branchId, request.signal);
        if (request.signal.aborted) return;
        this.statistics = statistics;
      } else if (tab === "members") {
        const page = await this.api.listMembers(this.branchId, cursor, request.signal);
        if (request.signal.aborted) return;
        if (more && page.nextCursor === cursor)
          throw new ApiError("The member list did not advance. Reload to try again.");
        this.members = more ? merge(this.members ?? [], page.items) : page.items;
        this.cursors.members = page.nextCursor;
      } else if (tab === "leaflets") {
        const page = await this.api.listLeaflets(this.branchId, cursor, request.signal);
        if (request.signal.aborted) return;
        if (more && page.nextCursor === cursor)
          throw new ApiError("The leaflet list did not advance. Reload to try again.");
        this.leaflets = more ? merge(this.leaflets ?? [], page.items) : page.items;
        this.cursors.leaflets = page.nextCursor;
      } else {
        const page = await this.api.listCommunications(this.branchId, cursor, request.signal);
        if (request.signal.aborted) return;
        if (more && page.nextCursor === cursor)
          throw new ApiError("The communication list did not advance. Reload to try again.");
        this.communications = more ? merge(this.communications ?? [], page.items) : page.items;
        this.cursors.communications = page.nextCursor;
      }
    } catch (error) {
      if (!request.signal.aborted && !this.handleFailure(error)) load.error = message(error);
    } finally {
      if (load.request === request) {
        load.request = null;
        this.renderTabUpdate();
      }
    }
  }

  private handleFailure(error: unknown): boolean {
    if (error instanceof AuthenticationRequiredError || this.authentication.account === null) {
      this.request?.abort();
      this.request = null;
      this.clearData();
      this.branches = null;
      this.organizations = null;
      this.branchId = null;
      this.busy = false;
      this.renderSignIn(message(error));
      return true;
    }
    if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
      this.request?.abort();
      this.request = null;
      this.busy = false;
      this.clearData();
      this.branchId = null;
      this.error = message(error);
      this.renderDashboard();
      return true;
    }
    return false;
  }

  private async signOut(): Promise<void> {
    if (this.workspace !== null && !(await this.closeWorkspace())) return;
    this.request?.abort();
    this.request = null;
    this.clearData();
    this.branches = null;
    this.organizations = null;
    this.branchId = null;
    this.busy = false;
    this.activeTab = "members";
    this.renderMessage("Signing out", "Please wait a moment.");
    try {
      await this.authentication.signOut();
      this.renderSignIn();
    } catch (error) {
      this.renderSignIn(message(error));
    }
  }

  private replaceRoot(...content: readonly HTMLElement[]): void {
    this.root.replaceChildren(...content);
    this.root.querySelector("main")?.setAttribute("id", "main-content");
  }

  private renderSignIn(error?: string): void {
    this.replaceRoot(
      authenticationScreen(this.authentication, () => this.reload(false), {
        appName: "Branch",
        subtitle: "Manage your branch",
        error,
      }),
    );
  }

  private renderMessage(title: string, detail: string, action?: HTMLElement): void {
    this.replaceRoot(
      element(
        "main",
        "centred-screen",
        element(
          "section",
          "message-card",
          heading(1, title, "message-card__title"),
          element("p", "muted", detail),
          action,
        ),
      ),
    );
  }

  private renderDashboard(): void {
    const account = this.authentication.account;
    if (account === null) return;
    if (account.role === "member") {
      this.renderMessage(
        "Organiser access required",
        "Branch organisers, administrators and superadmins can use branch management.",
        button("Sign out", "button button--dark-blue", () => this.signOut()),
      );
      return;
    }
    const main = element("main", "dashboard branch-dashboard");
    main.id = "main-content";
    main.append(
      element(
        "section",
        "dashboard__intro",
        heading(1, "Branch administration", "dashboard__title"),
      ),
    );
    const controls = element("div", "branch-controls");
    if (account.role === "superadmin") {
      const organizations = this.organizations ?? [account.organization];
      controls.append(
        dashboardSelector(
          "Organisation",
          organizations,
          account.organization.id,
          this.busy || organizations.length === 0,
          (select) => this.selectOrganization(select.value),
        ),
      );
    }
    if (account.capabilities.administerOrganization && this.branches !== null) {
      controls.append(
        dashboardSelector(
          "Branch",
          this.branches,
          this.branchId ?? "",
          this.busy || this.branches.length === 0,
          (select) => this.selectBranch(select.value),
        ),
      );
    }
    if (controls.childElementCount > 0) main.append(controls);
    if (this.error !== null) {
      const alert = element(
        "div",
        "alert",
        element("p", null, this.error),
        button("Retry", "button button--white", () => this.reload(), this.busy),
      );
      alert.setAttribute("role", "alert");
      main.append(alert);
    }
    if (this.busy) {
      const status = element("p", "branch-loading", "Loading branch data…");
      status.setAttribute("role", "status");
      main.append(status);
    }
    if (this.branchId !== null && this.branches !== null) {
      main.append(this.tabs(), this.panel());
    } else if (!this.busy && this.error === null) {
      main.append(
        this.emptyState(
          "No branches available",
          "A branch can be created in the administration app.",
        ),
      );
    }
    this.replaceRoot(this.header(account), main);
  }

  private header(account: Account): HTMLElement {
    return dashboardHeader(
      createBrandGraphic(document, { tone: "light" }),
      account.user.displayName,
      `${account.organization.name} · ${account.role}`,
      button("Sign out", "button button--white", () => this.signOut()),
    );
  }

  private tabs(): HTMLElement {
    return dashboardTabs<Tab>(
      "Branch sections",
      [
        {
          id: "members",
          label: "Members",
          count:
            this.members === null
              ? undefined
              : `${this.members.length}${this.cursors.members === null ? "" : "+"}`,
        },
        { id: "communications", label: "Communications" },
        { id: "leaflets", label: "Leaflets" },
        { id: "statistics", label: "Statistics" },
      ],
      this.activeTab,
      (tab) => this.selectTab(tab),
    );
  }

  private async selectTab(tab: Tab): Promise<void> {
    if (this.workspace !== null && !(await this.closeWorkspace())) return;
    this.history?.destroy();
    this.history = null;
    this.closeLeafletAnalytics();
    this.activeTab = tab;
    this.renderDashboard();
    this.root.querySelector<HTMLButtonElement>(`#tab-${tab}`)?.focus();
    const data = {
      members: this.members,
      communications: this.communications,
      leaflets: this.leaflets,
      statistics: this.statistics,
    };
    if (data[tab] === null) await this.loadTab(tab);
  }

  private renderTabUpdate(): void {
    const focusedTab = document.activeElement?.closest('[role="tab"]')?.id;
    this.renderDashboard();
    if (focusedTab !== undefined)
      this.root.querySelector<HTMLButtonElement>(`#${focusedTab}`)?.focus();
  }

  private panel(): HTMLElement {
    const panel = element("section", "data-panel");
    panel.id = `panel-${this.activeTab}`;
    panel.setAttribute("role", "tabpanel");
    panel.setAttribute("aria-labelledby", `tab-${this.activeTab}`);
    const tab = this.activeTab;
    const load = this.tabLoads[tab];
    panel.setAttribute("aria-busy", String(load.request !== null));
    if (load.error !== null) {
      const alert = element(
        "div",
        "alert",
        element("p", null, load.error),
        button("Retry", "button button--white", () => this.loadTab(tab)),
      );
      alert.setAttribute("role", "alert");
      panel.append(alert);
    }
    if (load.request !== null) {
      const status = element("p", "branch-loading", loadingMessages[tab]);
      status.setAttribute("role", "status");
      panel.append(status);
    }
    if (tab === "members") {
      if (this.members !== null) panel.append(this.membersPanel());
    } else if (tab === "statistics") {
      if (this.statistics !== null)
        panel.append(
          statisticsPanel(
            this.statistics,
            () => this.loadTab("statistics"),
            load.request !== null,
          ),
        );
    } else if (tab === "leaflets") {
      if (this.leafletWorkspace !== null) panel.append(this.leafletWorkspace.element);
      else if (this.leafletAnalytics !== null) panel.append(this.leafletAnalytics.element);
      else if (this.leaflets !== null) panel.append(this.leafletsPanel());
    } else if (this.composer !== null) panel.append(this.composer.element);
    else if (this.history !== null) panel.append(this.history.element);
    else if (this.communications !== null) panel.append(this.communicationsPanel());
    return panel;
  }

  private membersPanel(): HTMLElement {
    const members = this.members ?? [];
    const section = element(
      "div",
      null,
      element("header", "panel-heading", heading(2, "Branch members", "panel-heading__title")),
    );
    if (members.length === 0)
      section.append(
        this.emptyState(
          "No members yet",
          "Members will appear here when they join this branch.",
        ),
      );
    else section.append(memberTable(members));
    if (this.cursors.members !== null) section.append(this.moreButton("members"));
    return section;
  }

  private communicationsPanel(): HTMLElement {
    const actions = element("div", "communication-actions");
    for (const channel of channels) {
      const action = button(
        channelLabels[channel],
        "button button--dark-blue",
        () => this.compose(channel),
        this.busy || this.tabLoads.communications.request !== null,
      );
      action.prepend(channelIcon(channel));
      actions.append(action);
    }
    const history = element(
      "section",
      null,
      element(
        "header",
        "panel-heading",
        heading(2, "Communication history", "panel-heading__title"),
      ),
    );
    const communications = this.communications ?? [];
    if (communications.length === 0)
      history.append(
        this.emptyState(
          "No communications yet",
          "Drafts and submitted emails, texts and app notifications will appear here.",
        ),
      );
    else
      history.append(
        table(
          ["Message", "Channel", "Status", "Recipients", "Updated"],
          communications.map((item) => {
            const body = item.channel === "email" ? emailDisplayText(item.body) : item.body;
            const title =
              item.title?.trim() ||
              body.slice(0, 100).trim() ||
              `Untitled ${channelLabels[item.channel].toLowerCase()}`;
            return {
              action: {
                label: `Open ${title}`,
                onSelect: () => this.openCommunication(item),
                disabled: this.busy || this.tabLoads.communications.request !== null,
              },
              cells: [
                title,
                channelLabels[item.channel],
                item.submittedAt === null
                  ? "Draft"
                  : item.channel === "app_notification"
                    ? "In app inboxes"
                    : "Submitted",
                String(item.recipientCount),
                times.format(item.updatedAt),
              ],
            };
          }),
        ),
      );
    if (this.cursors.communications !== null) history.append(this.moreButton("communications"));
    return element(
      "div",
      null,
      element(
        "section",
        "panel-heading",
        heading(2, "Send a communication", "panel-heading__title"),
        actions,
      ),
      history,
    );
  }

  private compose(channel: Channel): Promise<void> {
    return this.openWorkspace(
      (branchId, signal) => this.api.createDraft(branchId, channel, signal),
      (draft) => this.editDraft(draft),
    );
  }

  private openCommunication(item: Communication): void | Promise<void> {
    if (this.busy || this.branchId === null) return;
    if (item.submittedAt === null) return this.openDraft(item.id);
    this.history?.destroy();
    this.history = new CommunicationHistory(
      item,
      this.branchId,
      this.api,
      () => this.selectTab("communications"),
      (error) => this.handleFailure(error),
    );
    this.renderDashboard();
    const title = this.history.element.querySelector("h2");
    if (title !== null) {
      title.tabIndex = -1;
      title.focus({ preventScroll: true });
    }
    this.history.element.scrollIntoView({ block: "start" });
    void this.history.load();
  }

  private openDraft(id: string): Promise<void> {
    return this.openWorkspace(
      (branchId, signal) => this.api.getDraft(branchId, id, signal),
      (draft) => this.editDraft(draft),
    );
  }

  /** Loads a draft or leaflet, then opens it for editing in place of the list. */
  private async openWorkspace<T>(
    load: (branchId: string, signal: AbortSignal) => Promise<T>,
    open: (value: T) => void,
  ): Promise<void> {
    if (this.busy || this.branchId === null) return;
    const request = this.beginRequest();
    this.renderDashboard();
    try {
      const value = await load(this.branchId, request.signal);
      if (request.signal.aborted) return;
      open(value);
    } catch (error) {
      if (!request.signal.aborted && !this.handleFailure(error)) this.error = message(error);
    } finally {
      if (this.request === request) {
        this.busy = false;
        this.renderDashboard();
        this.focusWorkspace();
      }
    }
  }

  private editDraft(draft: CommunicationDraft): void {
    if (this.branchId === null) return;
    this.rememberDraft(draft);
    this.composer?.destroy();
    this.composer = new CommunicationComposer(
      draft,
      this.branchId,
      this.api,
      () => this.selectTab("communications"),
      (saved) => this.rememberDraft(saved),
      () => this.openDraft(draft.id),
      (error) => this.handleFailure(error),
      (sent) => {
        this.communications = merge(this.communications ?? [], [sent]).toSorted(
          (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime() || b.id.localeCompare(a.id),
        );
        this.composer?.destroy();
        this.composer = null;
        this.renderDashboard();
      },
      () => this.draftDeleted(draft.id),
    );
  }

  /** Drafts start at their first field; leaflets at their title while the editor loads. */
  private focusWorkspace(): void {
    const workspace = this.workspace?.element;
    if (workspace === undefined || !workspace.isConnected) return;
    if (this.composer !== null)
      workspace.querySelector<HTMLElement>("input, textarea")?.focus({ preventScroll: true });
    else {
      const title = workspace.querySelector<HTMLElement>("h2");
      if (title !== null) {
        title.tabIndex = -1;
        title.focus({ preventScroll: true });
      }
    }
    workspace.scrollIntoView({ block: "start" });
  }

  private rememberDraft(draft: CommunicationDraft): void {
    this.communications = merge(this.communications ?? [], [draft]).toSorted(
      (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime() || b.id.localeCompare(a.id),
    );
  }

  private get workspace(): CommunicationComposer | LeafletWorkspace | null {
    return this.composer ?? this.leafletWorkspace;
  }

  /** Saves and closes an open draft or leaflet; `false` keeps it open. */
  private async closeWorkspace(): Promise<boolean> {
    const workspace = this.workspace;
    if (workspace === null) return true;
    if (this.leavingWorkspace) return false;
    this.leavingWorkspace = true;
    try {
      if (!(await workspace.save()) || this.workspace !== workspace) return false;
      workspace.destroy();
      this.composer = null;
      this.leafletWorkspace = null;
      return true;
    } finally {
      this.leavingWorkspace = false;
    }
  }

  private leafletsPanel(): HTMLElement {
    const disabled = this.busy || this.tabLoads.leaflets.request !== null;
    const section = element(
      "div",
      null,
      element(
        "header",
        "panel-heading leaflet-list__heading",
        heading(2, "Leaflets", "panel-heading__title"),
        button("New leaflet", "button button--dark-blue", () => this.createLeaflet(), disabled),
      ),
    );
    const leaflets = this.leaflets ?? [];
    if (leaflets.length === 0)
      section.append(
        this.emptyState(
          "No leaflets yet",
          "Create a leaflet to design its front and back pages.",
        ),
      );
    else
      section.append(
        table(
          [
            "Name",
            "Paper size",
            "Scans",
            "Updated",
            { label: "", cellClassName: "data-table__action" },
          ],
          leaflets.map((leaflet) => {
            const analytics = button(
              "Analytics",
              "button button--white",
              () => this.openLeafletAnalytics(leaflet),
              disabled,
            );
            analytics.prepend(buttonIcon("analytics"));
            analytics.setAttribute("aria-label", `Scan analytics for ${leaflet.name}`);
            const remove = button(
              "Delete",
              "button button--white",
              () => this.deleteLeaflet(leaflet),
              disabled,
            );
            remove.prepend(buttonIcon("delete"));
            remove.setAttribute("aria-label", `Delete ${leaflet.name}`);
            return {
              action: {
                label: `Open ${leaflet.name}`,
                onSelect: () => this.openLeaflet(leaflet.id),
                disabled,
              },
              cells: [
                leaflet.name,
                paperSizeLabels[leaflet.paperSize],
                numbers.format(leaflet.scans),
                times.format(leaflet.updatedAt),
                element("div", "data-table__buttons", analytics, remove),
              ],
            };
          }),
        ),
      );
    if (this.cursors.leaflets !== null) section.append(this.moreButton("leaflets"));
    return section;
  }

  private async createLeaflet(): Promise<void> {
    if (this.busy || this.branchId === null) return;
    const branchId = this.branchId;
    const request = new AbortController();
    const leaflet = await newLeafletDialog(async (name, paperSize: PaperSize, destination) => {
      try {
        return await this.api.createLeaflet(
          branchId,
          name,
          paperSize,
          destination,
          request.signal,
        );
      } catch (error) {
        if (this.handleFailure(error)) request.abort();
        throw error;
      }
    }, request.signal);
    if (leaflet === null || this.branchId !== branchId) return;
    this.rememberLeaflet(leaflet);
    await this.openLeaflet(leaflet.id);
  }

  private openLeaflet(id: string): Promise<void> {
    return this.openWorkspace(
      (branchId, signal) => this.api.getLeaflet(branchId, id, signal),
      (leaflet) => this.editLeaflet(leaflet),
    );
  }

  private editLeaflet(leaflet: LeafletDocument): void {
    if (this.branchId === null) return;
    this.rememberLeaflet(leaflet);
    this.closeLeafletAnalytics();
    this.leafletWorkspace?.destroy();
    this.leafletWorkspace = new LeafletWorkspace(
      leaflet,
      this.branchId,
      this.api,
      () => this.selectTab("leaflets"),
      (saved) => this.rememberLeaflet(saved),
      () => this.openLeaflet(leaflet.id),
      (error) => this.handleFailure(error),
    );
  }

  private rememberLeaflet({
    id,
    name,
    paperSize,
    revision,
    updatedAt,
    qrCode,
    scans,
  }: Leaflet): void {
    this.leaflets = merge(this.leaflets ?? [], [
      { id, name, paperSize, revision, updatedAt, qrCode, scans },
    ]).toSorted(
      (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime() || b.id.localeCompare(a.id),
    );
  }

  /** Shows how often a leaflet's QR code was scanned, in place of the list. */
  private openLeafletAnalytics(leaflet: Leaflet): void {
    if (this.busy || this.branchId === null) return;
    this.closeLeafletAnalytics();
    const analytics = new LeafletAnalytics(
      leaflet,
      this.branchId,
      this.api,
      () => this.selectTab("leaflets"),
      () => this.openLeaflet(leaflet.id),
      (current) => this.changeLeafletSettings(current),
      (error) => this.handleFailure(error),
    );
    this.leafletAnalytics = analytics;
    this.renderDashboard();
    const title = analytics.element.querySelector<HTMLElement>("h2");
    if (title !== null) {
      title.tabIndex = -1;
      title.focus({ preventScroll: true });
    }
    analytics.element.scrollIntoView({ block: "start" });
    void analytics.load();
  }

  private closeLeafletAnalytics(): void {
    this.leafletAnalytics?.destroy();
    this.leafletAnalytics = null;
  }

  private async changeLeafletSettings(leaflet: Leaflet): Promise<void> {
    if (this.busy || this.branchId === null) return;
    const branchId = this.branchId;
    const request = new AbortController();
    const saved = await leafletSettingsDialog(
      leaflet,
      async (destination) => {
        try {
          return await this.api.setLeafletQrDestination(
            branchId,
            leaflet.id,
            destination,
            request.signal,
          );
        } catch (error) {
          if (this.handleFailure(error)) request.abort();
          throw error;
        }
      },
      request.signal,
    );
    if (saved === null || this.branchId !== branchId) return;
    this.rememberLeaflet(saved);
    this.leafletAnalytics?.update(saved);
  }

  private async deleteLeaflet(leaflet: Leaflet): Promise<void> {
    if (this.busy || this.branchId === null) return;
    const branchId = this.branchId;
    const confirmation = new AbortController();
    const confirmed = await confirmDeletion(
      `delete-leaflet-${leaflet.id}`,
      "Delete leaflet?",
      `“${leaflet.name}” and its scan analytics will be permanently deleted, and its QR code will stop working, even on printed copies. This cannot be undone.`,
      "Delete leaflet",
      confirmation.signal,
    );
    if (!confirmed || this.busy || this.branchId !== branchId) return;
    const request = this.beginRequest();
    this.renderDashboard();
    let stale = false;
    try {
      await this.api.deleteLeaflet(branchId, leaflet.id, leaflet.revision, request.signal);
      if (request.signal.aborted) return;
      this.leaflets = (this.leaflets ?? []).filter((item) => item.id !== leaflet.id);
    } catch (error) {
      if (request.signal.aborted || this.handleFailure(error)) return;
      this.error = message(error);
      stale = error instanceof ApiError && error.status === 409;
    } finally {
      if (this.request === request) {
        this.busy = false;
        this.renderDashboard();
      }
    }
    if (stale) {
      this.leaflets = null;
      this.cursors.leaflets = null;
      await this.loadTab("leaflets");
    }
  }

  private async draftDeleted(id: string): Promise<void> {
    if (this.branchId === null) return;
    this.composer?.destroy();
    this.composer = null;
    this.communications = (this.communications ?? []).filter((item) => item.id !== id);
    this.cursors.communications = null;
    await this.loadTab("communications");
  }

  private moreButton(tab: ListTab): HTMLElement {
    return element(
      "div",
      "load-more",
      button(
        "Load more",
        "button button--light-blue",
        () => this.loadTab(tab, true),
        this.busy || this.tabLoads[tab].request !== null,
      ),
    );
  }

  private emptyState(title: string, detail: string): HTMLElement {
    return element("div", "empty-state", heading(3, title), element("p", "muted", detail));
  }
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function merge<T extends { readonly id: string }>(
  current: readonly T[],
  next: readonly T[],
): readonly T[] {
  return [...new Map([...current, ...next].map((item) => [item.id, item])).values()];
}
